import { FC, useContext, useMemo, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import ReactConfetti from 'react-confetti';
import { noop } from 'lodash';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Check, Close, Remove, Star } from '@mui/icons-material';

import { LayoutContext } from '@shared/contexts/LayoutContext';

import { SwipableCardStack } from '@modules/Assertions/components/SwipableCardStack';
import { SwipableCard } from '@modules/Assertions/components/SwipableCard';
import { useAssertionBgImgs } from '@modules/Assertions/hooks/useAssertions';
import baseimage from '@modules/Assertions/components/assertion_base.webp';

import { useFeedbackCrud } from '../hooks/useFeedbackCrud';

import { FeedbackQuestionCard } from './FeedbackQuestionCard';


interface Props {
  session: Feedback.Session;
  onComplete?: () => void;
}

type SwipeDirection = 'left' | 'right' | 'up' | 'down';

interface AnswerOption {
  value: Feedback.AnswerValue;
  direction: SwipeDirection;
  label: string;
  swipeLabel: string;
  color: 'error' | 'success' | 'info' | 'inherit';
  icon: React.ReactNode;
}

const ANSWER_OPTIONS: AnswerOption[] = [
  { value: 'NO', direction: 'left', label: 'Disagree', swipeLabel: 'Disagree', color: 'error', icon: <Close /> },
  { value: 'NOT_SURE', direction: 'down', label: 'Neutral', swipeLabel: 'Neutral', color: 'inherit', icon: <Remove /> },
  { value: 'YES', direction: 'right', label: 'Agree', swipeLabel: 'Agree', color: 'success', icon: <Check /> },
  { value: 'ABSOLUTELY', direction: 'up', label: 'Strongly Agree', swipeLabel: 'Strongly Agree', color: 'info', icon: <Star /> },
];

export const FeedbackSessionSwipableComponent: FC<Props> = ({ session, onComplete }) => {
  const { isDesktop } = useContext(LayoutContext);
  const { width, height } = useWindowSize();

  const { answerQuestion } = useFeedbackCrud();
  const { assertionsBgImgs } = useAssertionBgImgs();

  // Synchronous guard: React state updates are lazy, so a ref is the only
  // reliable way to block a second mutation fired by a rapid double-tap or a
  // simultaneous swipe + button click on the same question.
  const inFlightRef = useRef<Set<number>>(new Set());
  const [locallyAnswered, setLocallyAnswered] = useState<Set<number>>(new Set());
  const [showConfetti, setShowConfetti] = useState(false);

  // Merging server `answeredQuestionIds` with local state keeps a just-answered
  // question out of the queue during the API round-trip (prevents flicker).
  const pending = useMemo(() => {
    const serverAnswered = new Set(session.answeredQuestionIds);
    return session.questions.filter(q => !serverAnswered.has(q.id) && !locallyAnswered.has(q.id));
  }, [session, locallyAnswered]);

  const total = session.questions.length;
  const answeredCount = total - pending.length;
  const currentQuestion = pending[0];

  const availableImages = useMemo(
    () => (assertionsBgImgs || []).map(i => i?.image).filter(Boolean) as string[],
    [assertionsBgImgs],
  );

  const currentBgImage = useMemo(() => {
    if (!availableImages.length) return baseimage;
    return availableImages[answeredCount % availableImages.length];
  }, [availableImages, answeredCount]);

  const onAnswer = (questionId: number, answer: Feedback.AnswerValue) => {
    if (inFlightRef.current.has(questionId)) return;
    inFlightRef.current.add(questionId);

    setLocallyAnswered((prev) => {
      const next = new Set(prev);
      next.add(questionId);
      return next;
    });

    answerQuestion.mutate(
      { sessionId: session.id, questionId, answer },
      {
        onSuccess: (response) => {
          if (response.data.completed) {
            setShowConfetti(true);
            onComplete?.();
          }
        },
        onError: () => {
          inFlightRef.current.delete(questionId);
          setLocallyAnswered((prev) => {
            const next = new Set(prev);
            next.delete(questionId);
            return next;
          });
        },
      },
    );
  };

  const labelSize = isDesktop ? 120 : 42;
  const swipeLabelProps = (direction: SwipeDirection) => {
    const option = ANSWER_OPTIONS.find(o => o.direction === direction)!;
    return { label: option.swipeLabel, styles: { fontSize: labelSize } };
  };
  const rightOption = swipeLabelProps('right');
  const leftOption = swipeLabelProps('left');
  const upOption = swipeLabelProps('up');
  const downOption = swipeLabelProps('down');

  return (
    <Stack p={3}>
      {showConfetti && (
        <Box position="fixed" top={0} left={0} right={0} bottom={0} sx={{ pointerEvents: 'none', zIndex: 9999 }}>
          <ReactConfetti
            width={width}
            height={height}
            recycle={false}
            onConfettiComplete={() => setShowConfetti(false)}
          />
        </Box>
      )}

      {!currentQuestion ? (
        <Stack alignItems="center" justifyContent="center" p={4} spacing={2}>
          <Typography variant="h5">Thanks for your feedback!</Typography>
        </Stack>
      ) : (
        <>
          <Typography variant="body2" align="center" color="text.secondary" mb={1}>
            {answeredCount} / {total}
          </Typography>

          <Box position="relative" height={{ xs: '55vh', md: '60vh' }} maxHeight={600}>
            <SwipableCardStack key={currentQuestion.id} onFinish={noop}>
              <SwipableCard
                id={currentQuestion.id}
                onSwipeLeft={() => onAnswer(currentQuestion.id, 'NO')}
                onSwipeRight={() => onAnswer(currentQuestion.id, 'YES')}
                onSwipeDown={() => onAnswer(currentQuestion.id, 'NOT_SURE')}
                onSwipeUp={() => onAnswer(currentQuestion.id, 'ABSOLUTELY')}
                rightLabel={rightOption.label}
                rightLabelStyles={rightOption.styles}
                leftLabel={leftOption.label}
                leftLabelStyles={leftOption.styles}
                upLabel={upOption.label}
                upLabelStyles={upOption.styles}
                downLabel={downOption.label}
                downLabelStyles={downOption.styles}
              >
                <FeedbackQuestionCard question={currentQuestion} backgroundImage={currentBgImage} />
              </SwipableCard>
            </SwipableCardStack>
          </Box>

          {isDesktop && (
            <Stack direction="row" justifyContent="center" mt={3} spacing={2}>
              {ANSWER_OPTIONS.map(option => (
                <Button
                  key={option.value}
                  onClick={() => onAnswer(currentQuestion.id, option.value)}
                  color={option.color}
                  startIcon={option.icon}
                  variant="outlined"
                >
                  {option.label}
                </Button>
              ))}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};
